/**
 * SignupV2 — v2 reskin of auth/Signup (UI-v2 Phase 1e).
 *
 * ALL auth logic/hooks/mutations reused verbatim from v1 Signup.tsx.
 * Presentation only: AuthShellV2, shadcn primitives, FormFieldV2.
 * wide=true for multi-field layout (same as v1).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useSignup } from "@/hooks/queries/auth/useSignup";
import { TERMS_VERSION, PRIVACY_VERSION, DPA_VERSION } from "@/lib/legalVersions";
import { clientTimezone } from "@/lib/clientTimezone";
import { AuthShellV2 } from "@/components/v2/layout/AuthShellV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FormFieldV2 } from "@/components/v2/patterns/FormV2";

const PASSWORD_HELP = "At least 12 characters. Avoid common passwords.";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignupV2(): JSX.Element {
  const signup = useSignup();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedDpa, setAcceptedDpa] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState<string | null>(null);
  const [fullPageError, setFullPageError] = useState<string | null>(null);

  const passwordTooShort = password.length > 0 && password.length < 12;
  const passwordsMismatch = confirmPassword.length > 0 && confirmPassword !== password;
  const emailLooksWrong = email.length > 0 && !EMAIL_RE.test(email.trim());

  const canSubmit =
    acceptedTerms &&
    acceptedPrivacy &&
    acceptedDpa &&
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    !emailLooksWrong &&
    password.length >= 12 &&
    confirmPassword === password &&
    companyName.trim();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldErrors({});
    setFullPageError(null);

    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs["firstName"] = "First name is required.";
    if (!lastName.trim()) errs["lastName"] = "Last name is required.";
    if (!EMAIL_RE.test(email.trim())) errs["email"] = "Enter a valid email address.";
    if (password.length < 12)
      errs["password"] = "Password must be at least 12 characters.";
    if (!confirmPassword)
      errs["confirmPassword"] = "Please confirm your password.";
    else if (confirmPassword !== password)
      errs["confirmPassword"] = "Passwords don't match.";
    if (!companyName.trim()) errs["companyName"] = "Company name is required.";
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    signup.mutate(
      {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        password,
        company_name: companyName.trim(),
        company_website: companyWebsite.trim() || undefined,
        terms_version: TERMS_VERSION,
        privacy_version: PRIVACY_VERSION,
        dpa_version: DPA_VERSION,
        accepted_legal: true,
        ...clientTimezone(),
      },
      {
        onSuccess: () => {
          setSuccess(email);
        },
        onError: (err) => {
          console.error("signup failed", err);
          switch (err.error) {
            case "disposable_email_blocked":
              setFieldErrors({ email: "Use a work email — personal addresses aren't supported." });
              break;
            case "weak_password": {
              const reasons = Array.isArray(err.details["reasons"])
                ? (err.details["reasons"] as string[]).join(", ")
                : "Password is too weak.";
              setFieldErrors({ password: reasons });
              break;
            }
            case "validation_error": {
              const details = err.details as Record<string, unknown>;
              const fields = details["fields"];
              if (fields && typeof fields === "object") {
                const next: Record<string, string> = {};
                for (const [k, v] of Object.entries(fields as Record<string, unknown>)) {
                  if (typeof v === "string") {
                    const formKey =
                      k === "first_name" ? "firstName"
                      : k === "last_name" ? "lastName"
                      : k === "company_name" ? "companyName"
                      : k === "company_website" ? "companyWebsite"
                      : k;
                    next[formKey] = v;
                  }
                }
                setFieldErrors(next);
              } else {
                setFullPageError("Check the highlighted fields.");
              }
              break;
            }
            case "legal_version_mismatch":
              setFullPageError("Our terms updated. Reload to continue.");
              break;
            case "trial_rate_limited": {
              const days = typeof err.details["retry_after_days"] === "number"
                ? err.details["retry_after_days"]
                : null;
              setFieldErrors({
                email: `A trial was started recently with this email. Try again${days ? ` in ${days} day${days === 1 ? "" : "s"}` : " later"}.`,
              });
              break;
            }
            case "slug_reserved":
            case "slug_taken": {
              const suggestions = Array.isArray(err.details["suggestions"])
                ? (err.details["suggestions"] as string[]).join(", ")
                : "";
              setFieldErrors({
                companyName: `That company name is unavailable.${suggestions ? ` Try: ${suggestions}` : ""}`,
              });
              break;
            }
            default: {
              const backendMessage = err.message?.trim();
              if (backendMessage && backendMessage !== "Network Error") {
                setFullPageError(`${backendMessage} (code: ${err.error})`);
              } else {
                setFullPageError(
                  `Hit an error (code: ${err.error}). If it repeats, email support@certainhr.ai.`
                );
              }
            }
          }
        },
      }
    );
  };

  if (fullPageError) {
    return (
      <AuthShellV2 title="We hit an error.">
        <p className="text-sm text-text-secondary">{fullPageError}</p>
      </AuthShellV2>
    );
  }

  if (success) {
    return (
      <AuthShellV2 title="Check your email">
        <p className="text-sm text-text-secondary">
          We sent a verification link to{" "}
          <strong className="font-semibold text-text-primary">{success}</strong>.
          Open it to activate your account.
        </p>
      </AuthShellV2>
    );
  }

  return (
    <AuthShellV2
      title="Start your CertAIn trial"
      subtitle="Recruiting, when you need to be CertAIn."
      wide
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-text-link hover:text-text-link-hover"
          >
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormFieldV2
            label="First name"
            htmlFor="firstName-v2"
            error={fieldErrors["firstName"]}
          >
            <Input
              id="firstName-v2"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              aria-invalid={!!fieldErrors["firstName"] || undefined}
            />
          </FormFieldV2>
          <FormFieldV2
            label="Last name"
            htmlFor="lastName-v2"
            error={fieldErrors["lastName"]}
          >
            <Input
              id="lastName-v2"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              aria-invalid={!!fieldErrors["lastName"] || undefined}
            />
          </FormFieldV2>
        </div>
        <FormFieldV2
          label="Work email (this is your sign-in)"
          htmlFor="email-v2"
          error={
            fieldErrors["email"] ??
            (emailLooksWrong ? "Enter a valid email address." : undefined)
          }
        >
          <Input
            id="email-v2"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-invalid={!!fieldErrors["email"] || emailLooksWrong || undefined}
          />
        </FormFieldV2>
        <FormFieldV2
          label="Password"
          htmlFor="password-v2"
          error={
            fieldErrors["password"] ??
            (passwordTooShort ? "Password must be at least 12 characters." : undefined)
          }
          hint={!fieldErrors["password"] && !passwordTooShort ? PASSWORD_HELP : undefined}
        >
          <Input
            id="password-v2"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            aria-invalid={!!fieldErrors["password"] || passwordTooShort || undefined}
          />
        </FormFieldV2>
        <FormFieldV2
          label="Confirm password"
          htmlFor="confirmPassword-v2"
          error={
            fieldErrors["confirmPassword"] ??
            (passwordsMismatch ? "Passwords don't match." : undefined)
          }
        >
          <Input
            id="confirmPassword-v2"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            aria-invalid={!!fieldErrors["confirmPassword"] || passwordsMismatch || undefined}
          />
        </FormFieldV2>
        <FormFieldV2
          label="Company name"
          htmlFor="companyName-v2"
          error={fieldErrors["companyName"]}
        >
          <Input
            id="companyName-v2"
            type="text"
            autoComplete="organization"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
            aria-invalid={!!fieldErrors["companyName"] || undefined}
          />
        </FormFieldV2>
        <FormFieldV2 label="Company website (optional)" htmlFor="companyWebsite-v2">
          <Input
            id="companyWebsite-v2"
            type="url"
            autoComplete="url"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
            placeholder="https://example.com"
          />
        </FormFieldV2>

        {/* Legal checkboxes — v2 surface-inset block */}
        <div
          className="flex flex-col gap-2 rounded-lg border border-border-default bg-surface-inset"
          style={{ padding: "12px" }}
        >
          <Label className="flex cursor-pointer items-start gap-2 font-normal">
            <Checkbox
              id="terms-v2"
              checked={acceptedTerms}
              onCheckedChange={(v) => setAcceptedTerms(!!v)}
              className="mt-0.5"
            />
            <span className="text-text-secondary">
              I agree to the{" "}
              <a
                href="/legal/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-link underline hover:text-text-link-hover"
              >
                Terms of Service
              </a>
            </span>
          </Label>
          <Label className="flex cursor-pointer items-start gap-2 font-normal">
            <Checkbox
              id="privacy-v2"
              checked={acceptedPrivacy}
              onCheckedChange={(v) => setAcceptedPrivacy(!!v)}
              className="mt-0.5"
            />
            <span className="text-text-secondary">
              I agree to the{" "}
              <a
                href="/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-link underline hover:text-text-link-hover"
              >
                Privacy Policy
              </a>
            </span>
          </Label>
          <Label className="flex cursor-pointer items-start gap-2 font-normal">
            <Checkbox
              id="dpa-v2"
              checked={acceptedDpa}
              onCheckedChange={(v) => setAcceptedDpa(!!v)}
              className="mt-0.5"
            />
            <span className="text-text-secondary">
              I agree to the{" "}
              <a
                href="/legal/dpa"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-link underline hover:text-text-link-hover"
              >
                Data Processing Agreement
              </a>
            </span>
          </Label>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={!canSubmit || signup.isPending}
          aria-busy={signup.isPending}
        >
          {signup.isPending ? "Creating account…" : "Create account"}
        </Button>
      </form>
    </AuthShellV2>
  );
}
